/**
 * EntityImageTile
 *
 * The image square on a catalog record's form, laid out like the corporate
 * dashboard's (components/ui/image-thumbnail.tsx inside a dashed, rounded
 * tile): it fills its column up to 192px (corp's w-48 left column) and is
 * square.
 *
 *   - With an image: the thumbnail, and on hover (or keyboard focus) two round
 *     icon buttons at the top right — View (eye) opens a large preview with
 *     Square / Wide / Full views (corp image-preview-dialog), Edit (pencil)
 *     opens the EntityImagePicker.
 *   - Without one: an "Upload image / Max 10MB" tile that opens the picker.
 *   - Disabled with `disabledText`: a grey tile with that text (e.g. "Select a
 *     product type to display an image").
 *
 * `required` only draws the marker and the red empty state; the page still has
 * to check for an image in its save handler (and pass `error` to show why).
 * `suggested` is the amber version for an image that is only recommended
 * (corp's "— suggested", e.g. a facility image); it blocks nothing.
 *
 * Inside a locked FieldLockScope (a Locked Admin form) the image can be viewed
 * but not changed: no Edit button, no picker, and an empty tile reads "No
 * image" (no upload prompt, no required / suggested marker).
 */
import { useState, type ReactNode } from 'react';
import { Eye, ImageIcon, Maximize, Pencil, RectangleHorizontal, Square, Upload } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { cn } from '@/lib/utils';
import { EntityImagePicker } from './EntityImagePicker';
import { LABEL_CLASS, fieldClasses } from './DialogChrome';
import type { EntityImageType } from '@/services/entityImages.service';

export interface EntityImageTileProps {
  entityType: EntityImageType;
  /** The saved record's id; null = not saved yet (the picker only returns the URL). */
  entityId: number | null;
  /** The record's current image URL, or null/empty for none. */
  imageUrl: string | null | undefined;
  /** Called with the chosen URL (already assigned when `entityId` is set). */
  onSelected: (url: string) => void;
  /** Photo folder uploads are filed under; defaults to the entity's system folder. */
  folder?: string;
  label?: string;
  required?: boolean;
  /** Amber "— suggested" while empty: recommended, not required. */
  suggested?: boolean;
  /** Error text under the tile; also turns the tile red. */
  error?: string;
  /** Disabled tiles do not open the picker; `disabledText` replaces the empty prompt. */
  disabled?: boolean;
  disabledText?: string;
  className?: string;
  /** Fill the column (corp's alert image, a w-72 column) instead of stopping at 12rem. */
  fullWidth?: boolean;
  /** Extra content after the label, e.g. a "modified" pill. */
  labelExtra?: ReactNode;
}

export function EntityImageTile({
  entityType,
  entityId,
  imageUrl,
  onSelected,
  folder,
  label = 'Image',
  required,
  suggested,
  error,
  disabled,
  disabledText,
  className,
  fullWidth = false,
  labelExtra,
}: EntityImageTileProps) {
  const locked = useFieldLocked();
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(false);
  const url = imageUrl?.trim() || '';
  const name = (label || 'image').toLowerCase();
  // Corp's missImage: required and empty turns the whole field red, error or not.
  const missing = !url && !!required && !disabled && !locked;
  const emptyRed = !url && (!!error || missing);
  const emptyAmber = !url && !emptyRed && !!suggested && !disabled && !locked;
  const state = error || missing ? 'error' : emptyAmber ? 'warning' : 'none';

  return (
    <div className={cn('space-y-2', fieldClasses.wrapper(state), className)}>
      {label && (
        <span className={cn(LABEL_CLASS, fieldClasses.label(state))}>
          <span>
            {label}
            {required && ' *'}
            {(missing || (error && !url)) && <span className={fieldClasses.hint('error')}> — required</span>}
            {emptyAmber && <span className={fieldClasses.hint('warning')}> — suggested</span>}
          </span>
          {labelExtra}
        </span>
      )}
      <div
        className={cn(
          // Dashed only while it is a plain empty upload box; a required / suggested image gets a solid line.
          'w-full rounded-lg border-2 border-dashed border-slate-300',
          !fullWidth && 'max-w-48',
          (error || emptyRed) && cn('border-solid', fieldClasses.dashed('error')),
          emptyAmber && cn('border-solid', fieldClasses.dashed('warning')),
          // Locked and empty: not an upload box.
          locked && !url && !error && 'border-solid border-slate-200',
        )}
      >
        {url ? (
          <div className="group relative overflow-hidden rounded-lg" data-testid="entity-image-thumbnail">
            <img src={url} alt={label || ''} className="aspect-square w-full object-cover" />
            <div
              data-testid="entity-image-actions"
              className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
            >
              <button
                type="button"
                onClick={() => setViewing(true)}
                aria-label={`View ${name}`}
                title="View image"
                className="flex h-7 w-7 items-center justify-center rounded-full bg-black/50 transition-colors hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <Eye className="h-3.5 w-3.5 text-white" aria-hidden />
              </button>
              {!disabled && !locked && (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  aria-label={`Change ${name}`}
                  title="Change image"
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-black/50 transition-colors hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <Pencil className="h-3.5 w-3.5 text-white" aria-hidden />
                </button>
              )}
            </div>
          </div>
        ) : locked ? (
          <div className="flex aspect-square flex-col items-center justify-center rounded-lg px-3">
            <ImageIcon className="mb-2 h-8 w-8 text-slate-300" aria-hidden />
            <span className="text-center text-xs text-slate-400">No image</span>
          </div>
        ) : disabled && disabledText ? (
          <div className="flex aspect-square flex-col items-center justify-center rounded-lg bg-slate-50 px-3">
            <ImageIcon className="mb-2 h-8 w-8 text-slate-300" aria-hidden />
            <span className="text-center text-xs text-slate-400">{disabledText}</span>
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen(true)}
            aria-label={`Upload ${name}`}
            aria-invalid={error ? true : undefined}
            className={cn(
              'flex aspect-square w-full flex-col items-center justify-center rounded-lg transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-slate-50',
            )}
          >
            <Upload className={cn('mb-2 h-8 w-8', fieldClasses.icon(emptyRed ? 'error' : emptyAmber ? 'warning' : 'none'))} aria-hidden />
            <span className={cn('text-xs', emptyRed ? fieldClasses.message('error') : 'text-slate-500')}>Upload image</span>
            <span className="text-xs text-slate-400">Max 10MB</span>
          </button>
        )}
      </div>
      {error && <p className="text-xs font-medium text-red-500">{error}</p>}

      {!locked && (
        <EntityImagePicker
          entityType={entityType}
          entityId={entityId}
          folder={folder}
          open={open}
          onOpenChange={setOpen}
          onSelected={onSelected}
          currentImageUrl={url || null}
        />
      )}
      {url && <ImagePreviewModal open={viewing} onClose={() => setViewing(false)} src={url} alt={label || ''} />}
    </div>
  );
}

// ─── Preview ───────────────────────────────────────────────────────────────────

type ViewMode = 'square' | 'wide' | 'full';

const MODES: { key: ViewMode; label: string; icon: typeof Square }[] = [
  { key: 'square', label: 'Square', icon: Square },
  { key: 'wide', label: 'Wide', icon: RectangleHorizontal },
  { key: 'full', label: 'Full', icon: Maximize },
];

/** The large image preview (corp image-preview-dialog): Square, Wide or Full. */
export function ImagePreviewModal({
  open,
  onClose,
  src,
  alt = '',
}: {
  open: boolean;
  onClose: () => void;
  src: string;
  alt?: string;
}) {
  const [mode, setMode] = useState<ViewMode>('square');
  const close = () => {
    setMode('square');
    onClose();
  };
  return (
    <Modal isOpen={open} onClose={close} title="Image preview" size="lg" bodyClassName="flex flex-col gap-3">
      <div className="flex shrink-0 justify-center gap-1" role="group" aria-label="Preview shape">
        {MODES.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            aria-pressed={mode === key}
            onClick={() => setMode(key)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              mode === key ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {label}
          </button>
        ))}
      </div>
      <div className="flex h-[min(560px,65vh)] min-h-0 items-center justify-center overflow-hidden" data-testid="image-preview">
        {mode === 'square' && (
          <div className="aspect-square h-full max-w-full overflow-hidden rounded-lg">
            <img src={src} alt={alt} className="h-full w-full object-cover" />
          </div>
        )}
        {mode === 'wide' && (
          <div className="mx-auto aspect-video max-h-full w-full overflow-hidden rounded-lg">
            <img src={src} alt={alt} className="h-full w-full object-cover" />
          </div>
        )}
        {mode === 'full' && <img src={src} alt={alt} className="max-h-full max-w-full rounded-lg object-contain" />}
      </div>
    </Modal>
  );
}
